import SearchBoxLayout from "@/components/layout/SearchBoxLayout";
import { Button } from "@/components/ui/Button";
import { PermissionButton } from "@/components/ui/PermissionButton";
import ButtonGroup from "@/components/ui/ButtonGroup";
import DateOneBox from "@/components/ui/DateOneBox";
import DateSearchBtn from "@/components/ui/DateSearchBtn";
import { InputBox } from "@/components/ui/InputBox";
import { CheckBox } from "@/components/ui/CheckBox";
import { useState, useEffect, useMemo as useReactMemo } from "react";
import AddMemo from "@/components/modal/additional/memo/Add";
import MonthCalendar from "@/components/ui/MonthCalendar";
import { useMemoMonthList, useDeleteMemo, useDriverScheduleShared } from "@/hooks/additional/useMemo";
import { useUserInfo } from "@/hooks/member/useMember";
import type { Memo, DriverSchedule } from "@/interface/additional";
import { toast } from "@/lib/toast";

interface MemoData {
  id?: number;
  date: string;
  content: string;
  category: string;
  time: string;
  createdBy?: string;
  isDriverSchedule?: boolean; // 기사 일정 여부
  driverCode?: string; // 기사 코드
  driverName?: string; // 기사 이름
  title?: string; // 기사 일정 제목
}

const formatDate = (date: Date): string => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

export default function Memo() {
  const { data: userInfo } = useUserInfo();
  const { mutate: deleteMemoMutation } = useDeleteMemo();
  const [dateTime, setDateTime] = useState<string | undefined>(undefined);
  const [category, setCategory] = useState<string>("전체");
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [isAddMemoOpen, setIsAddMemoOpen] = useState<boolean>(false);
  const [inputKeyword, setInputKeyword] = useState<string>("");
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [currentMemo, setCurrentMemo] = useState<string>("");
  const [currentCategory, setCurrentCategory] = useState<string>("전체");
  const [rangeBtn, setRangeBtn] = useState("today");
  const [pendingDate, setPendingDate] = useState<string | null>(null);
  const [selectedMemos, setSelectedMemos] = useState<Set<number>>(new Set());
  const [memoToEdit, setMemoToEdit] = useState<MemoData | null>(null);

  // year와 month 추출
  const getYearAndMonth = (date: string) => {
    const d = new Date(date);
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  };

  // API에서 메모 데이터 가져오기
  const { year, month } = selectedDate
    ? getYearAndMonth(selectedDate)
    : { year: new Date().getFullYear(), month: new Date().getMonth() + 1 };
  // 데이터 조회
  const { data: memoList } = useMemoMonthList({ year, month, category, keyword: searchKeyword });
  // 공유된 기사 일정 조회
  const { data: driverScheduleList } = useDriverScheduleShared({ year, month });

  // API 데이터를 내부 형식으로 변환 (메모 + 기사 일정 통합)
  const memos: Record<string, MemoData[]> = useReactMemo(() => {
    const result: Record<string, MemoData[]> = {};

    // 메모 데이터 추가
    if (memoList) {
      memoList.forEach((memo: Memo) => {
        if (!result[memo.memoDate]) {
          result[memo.memoDate] = [];
        }
        result[memo.memoDate].push({
          id: memo.id,
          date: memo.memoDate,
          content: memo.content,
          category: memo.category,
          time: memo.time || "",
          createdBy: memo.createdBy,
          isDriverSchedule: false,
        });
      });
    }

    // 기사 일정 데이터 추가 (카테고리가 "전체" 또는 "기사일정"일 때만)
    if (driverScheduleList && (category === "전체" || category === "기사일정")) {
      driverScheduleList.forEach((schedule: DriverSchedule) => {
        if (!result[schedule.scheduleDate]) {
          result[schedule.scheduleDate] = [];
        }
        result[schedule.scheduleDate].push({
          id: schedule.id,
          date: schedule.scheduleDate,
          content: schedule.description || "",
          category: "기사일정",
          time: "",
          createdBy: schedule.driverCode,
          isDriverSchedule: true,
          driverCode: schedule.driverCode,
          driverName: schedule.driverName || undefined,
          title: schedule.title,
        });
      });
    }

    return result;
  }, [memoList, driverScheduleList, category]);

  const categoryOptions = [
    { value: "전체", label: "전체" },
    { value: "메모", label: "메모" },
    { value: "중요", label: "중요" },
    { value: "기사일정", label: "기사일정" },
  ];

  useEffect(() => {
    const todayStr = formatDate(new Date());

    setDateTime(todayStr); // DateOneBox 표시용
    setSelectedDate(todayStr); // 캘린더 선택용
    setSearchKeyword(""); // 초기 검색어
    setInputKeyword(""); // 초기 input 검색어
  }, []);

  const handleReset = () => {
    const todayStr = formatDate(new Date());

    setDateTime(todayStr);
    setSelectedDate(todayStr);
    setCategory("전체");
    setSearchKeyword("");
    setInputKeyword("");
    setRangeBtn("today");
    setPendingDate(null);
  };

  const handleSearch = () => {
    // 검색어 적용
    setSearchKeyword(inputKeyword);

    // 날짜 적용
    const commit = pendingDate ?? dateTime;
    if (commit) {
      setSelectedDate(commit);
      setPendingDate(null);
    }
  };

  const handleDateClick = (arg: any) => {
    console.log("Clicked date:", arg.dateStr);
    console.log("Date object:", arg.date);

    const dateStr = formatDate(new Date(arg.date));

    console.log("Formatted date:", dateStr);
    setSelectedDate(dateStr);
    setCurrentMemo("");
  };

  const handleDateTimeChange = (date: string | undefined) => {
    setDateTime(date);
    setPendingDate(date ?? null);
  };

  const addMemo = () => {
    if (!selectedDate || !currentMemo.trim()) return;

    const newMemo: MemoData = {
      date: selectedDate,
      content: currentMemo,
      category: currentCategory,
      time: new Date().toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" }),
    };

    setCurrentMemo("");
    // TODO: API 호출로 메모 추가
  };

  // 체크박스 토글
  const handleCheckboxChange = (memoId: number | undefined) => {
    if (!memoId) return;
    setSelectedMemos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(memoId)) {
        newSet.delete(memoId);
      } else {
        newSet.add(memoId);
      }
      return newSet;
    });
  };

  // 수정 버튼 핸들러
  const handleEditClick = () => {
    if (selectedMemos.size !== 1) {
      toast.warning("수정할 메모를 1개 선택해주세요.");
      return;
    }

    // 선택된 메모 찾기
    let foundMemo: MemoData | null = null;
    for (const date in memos) {
      const memo = memos[date].find(m => m.id && selectedMemos.has(m.id));
      if (memo) {
        foundMemo = memo;
        break;
      }
    }

    if (foundMemo) {
      setMemoToEdit(foundMemo);
      setIsAddMemoOpen(true);
    }
  };

  // 삭제 버튼 핸들러
  const handleDeleteClick = () => {
    if (selectedMemos.size === 0) {
      toast.warning("삭제할 메모를 선택해주세요.");
      return;
    }

    // 선택된 모든 메모 ID를 배열로 변환
    const idsToDelete = Array.from(selectedMemos);

    // 각 메모를 삭제
    idsToDelete.forEach(id => {
      deleteMemoMutation(id);
    });

    // 선택 해제
    setSelectedMemos(new Set());
  };

  return (
    <div className="flex flex-col h-full">
      <SearchBoxLayout>
        <div className="flex items-end">
          <DateOneBox label="기간" date={dateTime} onDateChange={handleDateTimeChange} />
          <div className="mr-3" />
          <DateSearchBtn
            mode="single"
            value={rangeBtn}
            onChangeValue={setRangeBtn}
            currentDate={dateTime ?? undefined}
            onDateSelect={({ startDate }) => {
              setDateTime(startDate);
              setSelectedDate(startDate);
              setPendingDate(null);
            }}
          />
          <div className="mr-3" />
          <ButtonGroup label="카테고리" options={categoryOptions} value={category} onChange={setCategory} />
          <div className="mr-3" />
          <InputBox
            label="검색어"
            className="w-50"
            value={inputKeyword}
            onChange={e => setInputKeyword(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                handleSearch();
              }
            }}
          />
          <div className="flex gap-[6px] ml-auto">
            <Button variant="reset" onClick={handleReset}>
              초기화
            </Button>
            <Button variant="search" onClick={handleSearch}>
              조회
            </Button>
          </div>
        </div>
      </SearchBoxLayout>
      <main className="flex-1 px-4 flex flex-col min-h-0 bg-white">
        <div className="flex ml-auto items-end mb-2 gap-[10px]">
          <PermissionButton
            permission="canCreate"
            variant="etc"
            onClick={() => {
              setMemoToEdit(null);
              setIsAddMemoOpen(true);
            }}
          >
            추가
          </PermissionButton>
          <PermissionButton permission="canUpdate" variant="etc" onClick={handleEditClick}>
            수정
          </PermissionButton>
          <PermissionButton permission="canDelete" variant="etc" onClick={handleDeleteClick}>
            삭제
          </PermissionButton>
        </div>

        {/* 캘린더 및 메모 영역 */}
        <div className="flex flex-1 gap-4 min-h-0 pb-4">
          {/* 왼쪽 캘린더 영역 - 2/3 비율 */}
          <div className="flex-[2] bg-white rounded-lg overflow-hidden min-h-0 h-full">
            <MonthCalendar
              monthDate={selectedDate ?? dateTime ?? formatDate(new Date())}
              selectedDate={selectedDate ?? undefined}
              memos={memos}
              onSelectDate={ds => {
                setSelectedDate(ds);
                setCurrentMemo("");
                // 필요 시 setDateTime(ds);
              }}
            />
          </div>

          {/* 오른쪽 메모 사이드바 */}
          <div className="flex-1 bg-white rounded-lg flex flex-col">
            {/* 헤더 */}
            <div className="bg-[#D0E7AC] border-b border-[#81b334] py-2 text-center rounded-t-lg">
              <span className="text-[#333] font-semibold">상세 내역</span>
            </div>

            {/* 내용 영역 */}
            <div className="flex-1 overflow-y-auto border-l border-r border-b border-gray-200 rounded-bl-lg rounded-br-lg">
              {selectedDate ? (
                memos[selectedDate]?.filter(memo => category === "전체" || memo.category === category).length > 0 ? (
                  <div className="flex flex-col">
                    {memos[selectedDate]
                      .filter(memo => category === "전체" || memo.category === category)
                      .map((memo, index) => {
                        const isMyMemo = memo.createdBy === userInfo?.name;
                        const isDriverSchedule = memo.isDriverSchedule;

                        // 카테고리별 컬러 설정
                        const getBarColor = () => {
                          if (isDriverSchedule) return "#10B981"; // 기사일정: 초록색
                          if (memo.category === "중요") return "#E92C2C"; // 중요: 빨간색
                          return "#0085FF"; // 메모: 파란색
                        };

                        return (
                          <div key={`${memo.isDriverSchedule ? "ds" : "memo"}-${index}`} className="relative p-4 border-b border-gray-200">
                            {/* 컬러 바 */}
                            <div
                              className="absolute top-0 left-0 h-full w-1.5"
                              style={{ backgroundColor: getBarColor() }}
                            />
                            <div className="flex justify-between items-start mb-3 ml-2">
                              <div className="flex-1 pr-4">
                                {/* 기사 일정인 경우 제목 표시 */}
                                {isDriverSchedule && memo.title && (
                                  <p className="text-[#10B981] font-semibold text-sm mb-1">[기사일정] {memo.title}</p>
                                )}
                                <p className="text-[#000] font-medium text-sm leading-relaxed">{memo.content || (isDriverSchedule ? "(설명 없음)" : "")}</p>
                              </div>
                              {/* 본인 메모만 체크박스 표시 (기사 일정은 체크박스 없음) */}
                              {!isDriverSchedule && isMyMemo && (
                                <CheckBox
                                  className="flex-shrink-0"
                                  checked={memo.id ? selectedMemos.has(memo.id) : false}
                                  onChange={() => handleCheckboxChange(memo.id)}
                                />
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-500 mt-10 justify-end">
                              <div className="flex items-center gap-1">
                                {isDriverSchedule ? (
                                  // 기사 일정 아이콘
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                                    />
                                  </svg>
                                ) : (
                                  // 일반 메모 아이콘
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                    />
                                  </svg>
                                )}
                                <span>
                                  {isDriverSchedule ? (memo.driverName || memo.driverCode) : memo.createdBy}
                                  {!isDriverSchedule && isMyMemo && " (본인)"}
                                </span>
                              </div>
                              {memo.time && (
                                <div className="flex items-center gap-1">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                  </svg>
                                  <span>{memo.time}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center min-h-[400px]">
                    <div className="w-16 h-16  rounded-lg flex items-center justify-center mb-4">
                      <img src="/icons/noMemo.svg" alt="메모 없음" className="w-full h-full" />
                    </div>
                    <p className="text-sm text-[#717680] font-bold">등록된 메모가 없습니다.</p>
                  </div>
                )
              ) : (
                <div className="flex-1 flex items-center justify-center min-h-[400px]">
                  <div className="text-center text-gray-500">
                    <svg
                      className="w-16 h-16 mx-auto mb-4 text-gray-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <p className="text-lg font-medium">날짜를 선택해주세요</p>
                    <p className="text-sm">
                      달력에서 날짜를 클릭하면
                      <br />
                      해당 날짜의 메모를 확인할 수 있습니다
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <AddMemo
        isOpen={isAddMemoOpen}
        onClose={() => {
          setIsAddMemoOpen(false);
          setMemoToEdit(null);
          setSelectedMemos(new Set());
        }}
        memoToEdit={memoToEdit}
      />
    </div>
  );
}
